// Number and date formats shared by the property page's report sections, written the way the Solar
// Opportunity Report writes them (10-serverless/120-pack/render.py gbp, num, mwh, month), so the page
// and its PDF export read the same.
import type { OwnedProperty } from "../api";

/** £1,234 (a minus sign for negatives), rounded to the pound. */
export const gbp = (n?: number | null) => {
  const v = n ?? 0;
  return `${v < 0 ? "−" : ""}£${Math.abs(Math.round(v)).toLocaleString("en-GB")}`;
};

/** Short money: £12k from £10,000 up, else as gbp. */
export const gbpShort = (n?: number | null) => {
  const v = n ?? 0;
  return Math.abs(v) >= 10000 ? `${v < 0 ? "−" : ""}£${Math.round(Math.abs(v) / 1000).toLocaleString("en-GB")}k` : gbp(v);
};

/** A whole number with thousands separators. */
export const num = (n?: number | null) => Math.round(n ?? 0).toLocaleString("en-GB");

/** kWh as whole MWh: 123,456 kWh → "123 MWh". */
export const mwh = (kwh?: number | null) => `${Math.round((kwh ?? 0) / 1000).toLocaleString("en-GB")} MWh`;

/** "2024-03" → "Mar 2024"; anything else → "". */
export const month = (ym?: string | null) => {
  if (!ym) return "";
  const [y, m] = String(ym).split("-").map(Number);
  if (!y || !m) return "";
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "short", year: "numeric" });
};

/** "2023-09" → "September 2023" (the roof imagery's date); anything else as it is. */
export const monthYear = (ym?: string | null) => {
  if (!ym) return "";
  const [y, m] = String(ym).split("-").map(Number);
  if (!y || !m) return String(ym);
  return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

/** An ISO date as "11 October 2026". */
export const longDay = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

/** A number as Python's ":g" prints it: no trailing zeros, no float noise (2.4000000001 → 2.4). */
export const g = (n?: number | null) => String(Number((n ?? 0).toPrecision(6)));

/** Who uses the electricity, as the report says it: "the centre" for leisure, else "the building". */
export const occupant = (sector?: string | null) => (sector === "leisure" ? "the centre" : "the building");

/** First letter raised. */
export const cap = (s?: string | null) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

/** How close a size-and-use estimate is, in plain words (never how it was worked out). */
const CONFIDENCE: Record<string, string> = {
  high: "a close estimate",
  medium: "a reasonable estimate",
  low: "a rough estimate",
};

/** A private building's estimate in neutral words: "Estimated from the building's size and use: a reasonable estimate." */
export function estimateWords(p: OwnedProperty) {
  const c = p.estimate?.confidence ? CONFIDENCE[p.estimate.confidence] : null;
  return `Estimated from the building's size and use${c ? `: ${c}` : ""}.`;
}
